// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {Component, jsx} from 'panel';
import type {VNode} from 'snabbdom';

import {ButtonEl} from 'common/components/button';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {hasSlot} from 'common/util/dom';
import {formatText} from 'common/util/format-text';

import {ButtonTheme} from '../button/constants';
import {getIcon} from './helpers';
import {getImage} from './helpers';
import type {CtaBanner} from './index';
import type {BannerAttrs, BannerTooltip} from './types';

const BodyTextClassName = `banner-body`;

function getTooltip(bannerTooltip: BannerTooltip): VNode {
  return bannerTooltip ? (
    <div sel='.banner-tooltip'>
      <a
        attrs={{
          href: bannerTooltip.href,
        }}
      >
        {bannerTooltip.text}
      </a>
      <TooltipEl
        attrs={{
          elref: `bannerTooltip`,
          placement: Placement.Top,
          size: DisplaySize.Small,
        }}
        props={{
          // It is important to explicitly null this out, otherwise the content
          // from the other TooltipEl from renderTooltip will be rendered in
          // the tooltip.
          lazyContentRenderer: null,
        }}
      >
        <div sel='.banner-info-tooltip'>{bannerTooltip.tooltipText}</div>
      </TooltipEl>
    </div>
  ) : null;
}

export function CoreBanner(banner: Component<unknown, BannerAttrs>, actionNode: VNode | null): VNode {
  const tooltip = getTooltip(banner.attr(`tooltip`));
  if (hasSlot(banner, `cta`)) {
    actionNode = <slot attrs={{name: `cta`}} />;
  }
  let bodyTextNode: VNode = null;
  if (hasSlot(banner, `body-text`)) {
    bodyTextNode = (
      <slot
        sel={`.${BodyTextClassName}`}
        attrs={{name: `body-text`}}
        class={{
          [`${banner.attr(`theme`)}-theme`]: true,
        }}
      />
    );
  } else if (banner.attr(`body-text`)) {
    bodyTextNode = (
      <div
        sel={`.${BodyTextClassName}`}
        class={{
          [`${banner.attr(`theme`)}-theme`]: true,
        }}
      >
        {formatText(
          banner.attr(`body-text`),
          {
            multiline: banner.attr(`multiline`),
          },
          tooltip ? [tooltip] : [],
        )}
      </div>
    );
  }
  return (
    <div
      sel='.banner-wrapper'
      class={{
        [`${banner.attr(`type`)}-type`]: true,
        [`${banner.attr(`theme`)}-theme`]: true,
        [`has-image`]: !!banner.attr(`img-src`),
      }}
    >
      <div
        sel='.banner-panel'
        class={{
          'has-cta': !!actionNode,
          [`${banner.attr(`size`)}-size`]: true,
          [`${banner.attr(`theme`)}-theme`]: true,
          [`${banner.attr(`type`)}-type`]: true,
          [`has-image`]: !!banner.attr(`img-src`),
          'body-only': Boolean(bodyTextNode) && !banner.attr(`header-text`),
        }}
      >
        <div sel='.banner-icon-wrapper'>
          {getImage(banner) ? (
            <img sel='.banner-image' attrs={{src: getImage(banner)}} />
          ) : hasSlot(banner, `icon`) ? (
            <div sel='.banner-title-icon' class={{'banner-copy-no-header': !banner.attr(`header-text`)}}>
              <slot attrs={{name: `icon`}} />
            </div>
          ) : getIcon(banner) ? (
            <SvgIconEl
              sel='.banner-title-icon'
              attrs={{
                icon: getIcon(banner),
              }}
              class={{'banner-copy-no-header': !banner.attr(`header-text`)}}
            />
          ) : null}
          {banner.attr(`show-close-button`) ? (
            <ButtonEl
              attrs={{
                theme: ButtonTheme.Tertiary,
                icon: `x`,
              }}
              on={{click: () => banner.helpers.handleCloseButtonClick()}}
            />
          ) : null}
        </div>

        <div
          sel='.banner-content'
          class={{
            [`${banner.attr(`theme`)}-theme`]: true,
          }}
        >
          <div
            sel='.banner-copy'
            class={{
              'banner-copy-no-header': !banner.attr(`header-text`),
              [`${banner.attr(`theme`)}-theme`]: true,
            }}
          >
            {banner.attr(`header-text`) ? (
              <header sel='.banner-header' class={{[`${banner.attr(`theme`)}-theme`]: true}}>
                {banner.attr(`header-text`)}
              </header>
            ) : null}
            {bodyTextNode}
            {banner.attr(`footer-text`) ? <footer sel='.banner-footer'>{banner.attr(`footer-text`)}</footer> : null}
          </div>
          {actionNode ? (
            <div
              sel='.banner-actions'
              class={{
                [`${banner.attr(`theme`)}-theme`]: true,
              }}
            >
              {actionNode}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function template(this: CtaBanner): VNode {
  const hasCta = this.attr(`ctas`) && this.attr(`ctas`).length > 0;
  const ctaNode = hasCta ? (
    <>
      {this.attr(`ctas`).map((cta) => {
        const isEmbedded = this.attr(`type`) === this.helpers.BannerType.Embedded;
        const isSmall = this.attr(`size`) === this.helpers.BannerSize.Small;
        const embeddedTheme = isSmall ? ButtonTheme.Tertiary : ButtonTheme.Secondary;
        const shownTheme = cta.theme || (isEmbedded ? embeddedTheme : undefined);
        const shouldShowXIcon = isSmall && cta.actionName === this.helpers.BannerAction.Dismiss;

        return (
          <ButtonEl
            sel='.cta-button'
            attrs={{
              theme: shownTheme,
              icon: shouldShowXIcon ? `x` : cta.icon,
              type: this.helpers.getButtonType(),
            }}
            class={{
              'banner-action-button-embedded': isEmbedded,
              [`${this.attr(`theme`)}-theme`]: true,
            }}
            on={{click: () => this.helpers.handleCtaButtonClick(cta)}}
          >
            {!shouldShowXIcon ? cta.text : null}
          </ButtonEl>
        );
      })}
    </>
  ) : null;
  return CoreBanner(this, ctaNode);
}
